import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  People,
  Search,
  Star,
} from "@mui/icons-material";
import api from "../../services/api";

// 会員ランク定義
const MEMBER_RANKS = [
  { id: "regular", name: "レギュラー", color: "default" },
  { id: "silver", name: "シルバー", color: "info" },
  { id: "gold", name: "ゴールド", color: "warning" },
  { id: "platinum", name: "プラチナ", color: "secondary" },
  { id: "vip", name: "VIP", color: "error" },
];

const CustomerSettings = () => {
  const { storeId } = useParams();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [rankFilter, setRankFilter] = useState("all");
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState({
    customerCode: "",
    name: "",
    nameKana: "",
    email: "",
    phone: "",
    gender: "",
    birthDate: "",
    memberRank: "regular",
    notes: "",
  });

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/customers`);
      // バックエンドは { customers: [...], pagination: {...} } 形式で返す
      const customerList = response.data?.customers || response.data || [];
      setCustomers(customerList);
      setFilteredCustomers(customerList);
    } catch (err) {
      console.error("顧客情報の取得に失敗しました:", err);
      if (err.response?.status !== 404) {
        setError("顧客情報の取得に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // フィルタリング
  useEffect(() => {
    let result = customers;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(term) ||
          c.nameKana?.toLowerCase().includes(term) ||
          c.email?.toLowerCase().includes(term) ||
          c.phone?.includes(term) ||
          c.customerCode?.includes(term)
      );
    }

    if (rankFilter !== "all") {
      result = result.filter((c) => c.memberRank === rankFilter);
    }

    setFilteredCustomers(result);
  }, [customers, searchTerm, rankFilter]);

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        customerCode: customer.customerCode || "",
        name: customer.name || "",
        nameKana: customer.nameKana || "",
        email: customer.email || "",
        phone: customer.phone || "",
        gender: customer.gender || "",
        birthDate: customer.birthDate || "",
        memberRank: customer.memberRank || "regular",
        notes: customer.notes || "",
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        customerCode: "",
        name: "",
        nameKana: "",
        email: "",
        phone: "",
        gender: "",
        birthDate: "",
        memberRank: "regular",
        notes: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCustomer(null);
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      if (editingCustomer) {
        await api.put(
          `/api/stores/${storeId}/customers/${editingCustomer.id}`,
          formData
        );
      } else {
        await api.post(`/api/stores/${storeId}/customers`, formData);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      handleCloseDialog();
      fetchCustomers();
    } catch (err) {
      console.error("顧客情報の保存に失敗しました:", err);
      setError(err.response?.data?.message || "顧客情報の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm("この顧客を削除しますか？")) return;

    try {
      await api.delete(`/api/stores/${storeId}/customers/${customerId}`);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      fetchCustomers();
    } catch (err) {
      console.error("顧客の削除に失敗しました:", err);
      setError("顧客の削除に失敗しました");
    }
  };

  const getRankChip = (rank) => {
    const rankInfo = MEMBER_RANKS.find((r) => r.id === rank) || MEMBER_RANKS[0];
    return (
      <Chip
        label={rankInfo.name}
        color={rankInfo.color}
        size="small"
        icon={<Star fontSize="small" />}
      />
    );
  };

  // 統計情報
  const stats = {
    total: customers.length,
    byRank: MEMBER_RANKS.map((rank) => ({
      ...rank,
      count: customers.filter((c) => c.memberRank === rank.id).length,
    })),
  };

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h4" gutterBottom>
        顧客管理（CRM）
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        顧客情報の登録・管理と会員ランク設定を行います。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          処理が完了しました
        </Alert>
      )}

      {/* タブ */}
      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{ mb: 3 }}
      >
        <Tab label="顧客一覧" icon={<People />} iconPosition="start" />
        <Tab label="統計" icon={<Star />} iconPosition="start" />
      </Tabs>

      {tabValue === 0 && (
        <>
          {/* 検索・フィルター */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    placeholder="名前・電話番号・メールで検索"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>会員ランク</InputLabel>
                    <Select
                      value={rankFilter}
                      onChange={(e) => setRankFilter(e.target.value)}
                      label="会員ランク"
                    >
                      <MenuItem value="all">すべて</MenuItem>
                      {MEMBER_RANKS.map((rank) => (
                        <MenuItem key={rank.id} value={rank.id}>
                          {rank.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    fullWidth
                  >
                    新規追加
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* 顧客一覧 */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>会員番号</TableCell>
                  <TableCell>名前</TableCell>
                  <TableCell>連絡先</TableCell>
                  <TableCell>ランク</TableCell>
                  <TableCell align="right">ポイント</TableCell>
                  <TableCell align="right">来店回数</TableCell>
                  <TableCell align="right">累計購入額</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      顧客が登録されていません
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.customerCode || "-"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {customer.name || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {customer.nameKana}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {customer.phone || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {customer.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{getRankChip(customer.memberRank)}</TableCell>
                      <TableCell align="right">
                        {(customer.availablePoints || 0).toLocaleString()} pt
                      </TableCell>
                      <TableCell align="right">
                        {(customer.visitCount || 0).toLocaleString()} 回
                      </TableCell>
                      <TableCell align="right">
                        ¥{(customer.totalSpent || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(customer)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(customer.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          {/* 総顧客数 */}
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  総顧客数
                </Typography>
                <Typography variant="h3">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* ランク別統計 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ランク別顧客数
                </Typography>
                <Grid container spacing={2}>
                  {stats.byRank.map((rank) => (
                    <Grid item xs={6} sm={2.4} key={rank.id}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center" }}>
                          {getRankChip(rank.id)}
                          <Typography variant="h4" sx={{ mt: 1 }}>
                            {rank.count}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stats.total > 0
                              ? `${((rank.count / stats.total) * 100).toFixed(1)}%`
                              : "0%"}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 顧客編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCustomer ? "顧客情報を編集" : "新規顧客登録"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="会員番号"
                value={formData.customerCode}
                onChange={handleChange("customerCode")}
                placeholder="自動生成可"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>会員ランク</InputLabel>
                <Select
                  value={formData.memberRank}
                  onChange={handleChange("memberRank")}
                  label="会員ランク"
                >
                  {MEMBER_RANKS.map((rank) => (
                    <MenuItem key={rank.id} value={rank.id}>
                      {rank.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="名前"
                value={formData.name}
                onChange={handleChange("name")}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="名前（かな）"
                value={formData.nameKana}
                onChange={handleChange("nameKana")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="電話番号"
                value={formData.phone}
                onChange={handleChange("phone")}
                type="tel"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="メールアドレス"
                value={formData.email}
                onChange={handleChange("email")}
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>性別</InputLabel>
                <Select
                  value={formData.gender}
                  onChange={handleChange("gender")}
                  label="性別"
                >
                  <MenuItem value="">未設定</MenuItem>
                  <MenuItem value="male">男性</MenuItem>
                  <MenuItem value="female">女性</MenuItem>
                  <MenuItem value="other">その他</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="生年月日"
                type="date"
                value={formData.birthDate}
                onChange={handleChange("birthDate")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="メモ"
                value={formData.notes}
                onChange={handleChange("notes")}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.name}
          >
            {saving ? <CircularProgress size={20} /> : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerSettings;
